import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { API, formatCurrency } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { 
  DOMAINS,
  PRESTATIONS,
  getDomainLabel,
  getPrestationLabel,
  getPrestationsForDomain,
  isCustomValue,
  UNIT_OPTIONS,
  getUnitLabel,
  TVA_RATES,
  getCategoryLabel,
  getCategoryGroup,
  buildServiceDescription
} from '../config/serviceCategories';
import { toast } from 'sonner';
import { Plus, Search, Edit2, Trash2, Wrench, Tag } from 'lucide-react';

const ServicesPage = () => {
  const navigate = useNavigate();
  const { company, canWrite } = useAuth();
  const isFranchise = company?.tva_regime === 'franchise';
  
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDomain, setFilterDomain] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingService, setEditingService] = useState(null);
  
  // Form state - 2 level cascade
  const [selectedDomain, setSelectedDomain] = useState('commun');
  const [selectedPrestation, setSelectedPrestation] = useState('');
  const [customDomain, setCustomDomain] = useState('');
  const [customPrestation, setCustomPrestation] = useState('');
  const [unitPriceHt, setUnitPriceHt] = useState('');
  const [selectedUnit, setSelectedUnit] = useState('hour');
  const [customUnit, setCustomUnit] = useState('');
  const [tvaRate, setTvaRate] = useState(isFranchise ? 0 : 20);
  const [notes, setNotes] = useState('');

  // Derived state
  const availablePrestations = useMemo(() => {
    return getPrestationsForDomain(selectedDomain);
  }, [selectedDomain]);

  const showCustomDomainInput = selectedDomain === 'autre';
  const showCustomPrestationInput = isCustomValue(selectedPrestation, availablePrestations);
  const showCustomUnitInput = selectedUnit === 'custom';

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API}/services`);
      setServices(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des services');
    }
    setLoading(false);
  };

  const resetForm = () => {
    setSelectedDomain(company?.secteur_activite && !company.secteur_activite.startsWith('custom_') ? company.secteur_activite : 'commun');
    setSelectedPrestation('');
    setCustomDomain('');
    setCustomPrestation('');
    setUnitPriceHt('');
    setSelectedUnit('hour');
    setCustomUnit('');
    setTvaRate(20);
    setNotes('');
    setEditingService(null);
  };

  const handleDomainChange = (value) => {
    setSelectedDomain(value);
    setSelectedPrestation('');
    setCustomPrestation('');
    if (value !== 'autre') {
      setCustomDomain('');
    }
  };

  const handlePrestationChange = (value) => {
    setSelectedPrestation(value);
    if (!isCustomValue(value, availablePrestations)) {
      setCustomPrestation('');
    }
  };

  const handleUnitChange = (value) => {
    setSelectedUnit(value);
    if (value !== 'custom') {
      setCustomUnit('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate
    if (!unitPriceHt) {
      toast.error('Veuillez saisir un prix');
      return;
    }
    if (showCustomDomainInput && !customDomain.trim()) {
      toast.error('Veuillez préciser le métier');
      return;
    }
    if (!selectedPrestation && !showCustomDomainInput) {
      toast.error('Veuillez sélectionner une prestation');
      return;
    }
    if (showCustomPrestationInput && !customPrestation.trim()) {
      toast.error('Veuillez préciser la prestation');
      return;
    }
    if (showCustomUnitInput && !customUnit.trim()) {
      toast.error('Veuillez préciser l\'unité');
      return;
    }

    // Build category and description
    let finalDomain = selectedDomain;
    let finalPrestation = selectedPrestation;
    let finalUnit = selectedUnit;

    if (showCustomDomainInput) {
      finalDomain = `custom_${customDomain.trim().toLowerCase().replace(/\s+/g, '_')}`;
      finalPrestation = customPrestation.trim() 
        ? `custom_${customPrestation.trim().toLowerCase().replace(/\s+/g, '_')}`
        : finalDomain;
    } else if (showCustomPrestationInput) {
      finalPrestation = `custom_${customPrestation.trim().toLowerCase().replace(/\s+/g, '_')}`;
    }

    if (showCustomUnitInput) {
      finalUnit = `custom_${customUnit.trim()}`;
    }

    // Build description
    const description = buildServiceDescription(
      finalDomain, 
      finalPrestation,
      showCustomDomainInput ? customDomain.trim() : null,
      showCustomPrestationInput ? customPrestation.trim() : null
    );

    const payload = {
      description,
      domain: finalDomain,
      category: finalPrestation,
      unit: finalUnit,
      unit_price_ht: parseFloat(unitPriceHt),
      tva_rate: isFranchise ? 0 : parseFloat(tvaRate),
      notes: notes.trim() || null
    };

    try {
      if (editingService) {
        await axios.put(`${API}/services/${editingService.id}`, payload);
        toast.success('Service modifié');
      } else {
        await axios.post(`${API}/services`, payload);
        toast.success('Service ajouté');
      }
      fetchServices();
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    }
  };

  const handleEdit = (service) => {
    setEditingService(service);
    
    let domain = service.domain || 'commun';
    let prestation = service.category || '';
    
    if (domain.startsWith('custom_')) {
      setSelectedDomain('autre');
      setCustomDomain(domain.replace('custom_', '').replace(/_/g, ' '));
    } else {
      setSelectedDomain(domain);
      setCustomDomain('');
    }

    const prestations = getPrestationsForDomain(domain);
    const isCustomPrest = prestation.startsWith('custom_') || !prestations.find(p => p.value === prestation);
    
    if (isCustomPrest && prestation) {
      const autreOption = prestations.find(p => p.isCustom);
      setSelectedPrestation(autreOption?.value || prestation);
      setCustomPrestation(prestation.startsWith('custom_') 
        ? prestation.replace('custom_', '').replace(/_/g, ' ')
        : prestation);
    } else {
      setSelectedPrestation(prestation);
      setCustomPrestation('');
    }

    const isCustomUnit = service.unit?.startsWith('custom_') || !UNIT_OPTIONS.find(u => u.value === service.unit);
    if (isCustomUnit && service.unit) {
      setSelectedUnit('custom');
      setCustomUnit(service.unit.startsWith('custom_') 
        ? service.unit.replace('custom_', '')
        : service.unit);
    } else {
      setSelectedUnit(service.unit || 'hour');
      setCustomUnit('');
    }

    setUnitPriceHt(service.unit_price_ht?.toString() || '');
    setTvaRate(service.tva_rate || 20);
    setNotes(service.notes || '');
    setDialogOpen(true);
  };

  const handleDelete = async (serviceId) => {
    try {
      await axios.delete(`${API}/services/${serviceId}`);
      toast.success('Service supprimé');
      fetchServices();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleOpenDialog = () => {
    if (canWrite()) setDialogOpen(true);
    else navigate('/subscription');
  };

  // Filter services
  const filteredServices = services.filter(service => {
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch = 
      service.description?.toLowerCase().includes(searchLower) ||
      getCategoryLabel(service.category)?.toLowerCase().includes(searchLower) ||
      getDomainLabel(service.domain)?.toLowerCase().includes(searchLower);
    
    if (filterDomain === 'all') return matchesSearch;
    return matchesSearch && (service.domain === filterDomain || getCategoryGroup(service.category)?.toLowerCase().includes(filterDomain));
  });

  // Group services by domain
  const groupedServices = useMemo(() => {
    const groups = {};
    filteredServices.forEach(service => {
      const groupName = service.domain 
        ? getDomainLabel(service.domain).toUpperCase()
        : getCategoryGroup(service.category);
      if (!groups[groupName]) groups[groupName] = [];
      groups[groupName].push(service);
    });
    return groups;
  }, [filteredServices]);

  if (loading) {
    return (
      <div className="page-container p-4">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-muted rounded w-1/3"></div>
          <div className="h-12 bg-muted rounded"></div>
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-20 bg-muted rounded-xl"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container p-4 md:p-8 noise-overlay">
      <div className="relative z-10 max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight uppercase">Catalogue</h1>
          <Button 
            onClick={handleOpenDialog}
            className="h-12 px-6 bg-primary text-primary-foreground hover:bg-accent font-bold uppercase tracking-wide rounded-full"
            data-testid="add-service-btn"
          >
            <Plus className="w-5 h-5 mr-2" />
            <span className="hidden sm:inline">Ajouter</span>
          </Button>
        </div>

        {/* Dialog (controlled, no DialogTrigger) */}
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogContent 
            onPointerDownOutside={(e) => e.preventDefault()}
            onInteractOutside={(e) => e.preventDefault()}
            className="bg-card border-white/10 max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold uppercase">
                {editingService ? 'Modifier le service' : 'Nouveau service'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              
              {/* Level 1: Métier / Domaine */}
              <div className="space-y-2">
                <Label className="text-primary font-semibold">1. Métier / Domaine</Label>
                <Select value={selectedDomain} onValueChange={handleDomainChange}>
                  <SelectTrigger className="h-12 bg-muted/50 border-transparent focus:border-primary" data-testid="service-domain-select">
                    <SelectValue placeholder="Choisir un métier..." />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-white/10 max-h-[300px]">
                    {DOMAINS.map(domain => (
                      <SelectItem 
                        key={domain.value} 
                        value={domain.value}
                        className={domain.isCustom ? 'text-muted-foreground italic' : ''}
                      >
                        {domain.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Custom domain input */}
              {showCustomDomainInput && (
                <div className="space-y-2 ml-4 border-l-2 border-primary/30 pl-4">
                  <Label htmlFor="custom_domain">Précisez le métier *</Label>
                  <Input
                    id="custom_domain"
                    value={customDomain}
                    onChange={(e) => setCustomDomain(e.target.value)}
                    className="h-12 bg-muted/50 border-transparent focus:border-primary"
                    placeholder="Ex: Coaching sportif, Cours de musique..."
                    data-testid="service-custom-domain-input"
                  />
                </div>
              )}

              {/* Level 2: Prestation */}
              <div className="space-y-2">
                <Label className="text-primary font-semibold">2. Prestation</Label>
                {showCustomDomainInput ? (
                  <Input
                    value={customPrestation}
                    onChange={(e) => setCustomPrestation(e.target.value)}
                    className="h-12 bg-muted/50 border-transparent focus:border-primary"
                    placeholder="Décrivez votre prestation..."
                    data-testid="service-custom-prestation-direct"
                  />
                ) : (
                  <Select value={selectedPrestation} onValueChange={handlePrestationChange}>
                    <SelectTrigger className="h-12 bg-muted/50 border-transparent focus:border-primary" data-testid="service-prestation-select">
                      <SelectValue placeholder="Choisir une prestation..." />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-white/10 max-h-[300px]">
                      {availablePrestations.map(prestation => (
                        <SelectItem 
                          key={prestation.value} 
                          value={prestation.value}
                          className={prestation.isCustom ? 'text-muted-foreground italic' : ''}
                        >
                          {prestation.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>

              {/* Custom prestation input */}
              {showCustomPrestationInput && !showCustomDomainInput && (
                <div className="space-y-2 ml-4 border-l-2 border-primary/30 pl-4">
                  <Label htmlFor="custom_prestation">Précisez la prestation *</Label>
                  <Input
                    id="custom_prestation"
                    value={customPrestation}
                    onChange={(e) => setCustomPrestation(e.target.value)}
                    className="h-12 bg-muted/50 border-transparent focus:border-primary"
                    placeholder="Décrivez votre prestation..."
                    data-testid="service-custom-prestation-input"
                  />
                </div>
              )}

              {/* Price and Unit */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="unit_price_ht" className="text-primary font-semibold">
                    3. {selectedUnit === 'forfait' ? 'Montant forfaitaire HT (€)' : 'Prix HT (€)'} *
                  </Label>
                  <Input
                    id="unit_price_ht"
                    type="number"
                    step="0.01"
                    min="0"
                    value={unitPriceHt}
                    onChange={(e) => setUnitPriceHt(e.target.value)}
                    className="h-12 bg-muted/50 border-transparent focus:border-primary"
                    placeholder={selectedUnit === 'forfait' ? '500.00' : '45.00'}
                    data-testid="service-price-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-primary font-semibold">4. Unité</Label>
                  <Select value={selectedUnit} onValueChange={handleUnitChange}>
                    <SelectTrigger className="h-12 bg-muted/50 border-transparent" data-testid="service-unit-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-white/10">
                      {UNIT_OPTIONS.map(unit => (
                        <SelectItem 
                          key={unit.value} 
                          value={unit.value}
                          className={unit.isCustom ? 'text-muted-foreground italic' : ''}
                        >
                          {unit.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Custom unit input */}
              {showCustomUnitInput && (
                <div className="space-y-2 ml-4 border-l-2 border-primary/30 pl-4">
                  <Label htmlFor="custom_unit">Précisez l'unité *</Label>
                  <Input
                    id="custom_unit"
                    value={customUnit}
                    onChange={(e) => setCustomUnit(e.target.value)}
                    className="h-12 bg-muted/50 border-transparent focus:border-primary"
                    placeholder="Ex: Kg, Litre, Session..."
                    data-testid="service-custom-unit-input"
                  />
                </div>
              )}

              {/* TVA Rate */}
              {!isFranchise && (
                <div className="space-y-2">
                  <Label className="text-primary font-semibold">5. Taux TVA</Label>
                  <Select 
                    value={tvaRate.toString()} 
                    onValueChange={(v) => setTvaRate(parseFloat(v))}
                  >
                    <SelectTrigger className="h-12 bg-muted/50 border-transparent" data-testid="service-tva-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-white/10">
                      {TVA_RATES.map(rate => (
                        <SelectItem key={rate.value} value={rate.value.toString()}>
                          {rate.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Notes */}
              <div className="space-y-2">
                <Label htmlFor="notes" className="text-muted-foreground">6. Note optionnelle</Label>
                <Textarea
                  id="notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="bg-muted/50 border-transparent focus:border-primary min-h-[80px]"
                  placeholder="Précisions, conditions particulières..."
                  data-testid="service-notes-input"
                />
              </div>

              <Button
                type="submit"
                className="w-full h-12 bg-primary text-primary-foreground hover:bg-accent font-bold uppercase tracking-wide rounded-full"
                data-testid="service-save-btn"
              >
                {editingService ? 'Enregistrer' : 'Ajouter le service'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* Search & Filter */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              placeholder="Rechercher un service..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 h-12 bg-muted/50 border-transparent focus:border-primary"
              data-testid="service-search-input"
            />
          </div>
          <Select value={filterDomain} onValueChange={setFilterDomain}>
            <SelectTrigger className="w-full sm:w-56 h-12 bg-muted/50 border-transparent" data-testid="service-filter-select">
              <SelectValue placeholder="Filtrer par métier" />
            </SelectTrigger>
            <SelectContent className="bg-card border-white/10 max-h-[400px]">
              <SelectItem value="all">Tous les métiers</SelectItem>
              {DOMAINS.filter(d => !d.isCustom).map(domain => (
                <SelectItem key={domain.value} value={domain.value}>
                  {domain.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Service List */}
        {filteredServices.length > 0 ? (
          <div className="space-y-6">
            {Object.entries(groupedServices).map(([groupName, groupServices]) => (
              <div key={groupName}>
                <div className="flex items-center gap-2 mb-3">
                  <Tag className="w-4 h-4 text-primary" />
                  <h2 className="text-lg font-semibold uppercase tracking-wide text-muted-foreground">
                    {groupName}
                  </h2>
                  <span className="text-sm text-muted-foreground">({groupServices.length})</span>
                </div>
                <div className="space-y-2">
                  {groupServices.map((service) => (
                    <Card key={service.id} className="bg-card border-white/10" data-testid={`service-card-${service.id}`}>
                      <CardContent className="p-4 flex items-center justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-foreground">
                            {getPrestationLabel(service.category, service.domain) || service.description}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {getDomainLabel(service.domain) || getCategoryGroup(service.category)}
                          </p>
                          <div className="flex flex-wrap items-center gap-2 mt-1">
                            {service.unit === 'forfait' ? (
                              <>
                                <span className="text-sm text-primary font-bold">Forfait</span>
                                <span className="text-xs text-muted-foreground">
                                  • Montant : {formatCurrency(service.unit_price_ht)} • TVA {service.tva_rate}%
                                </span>
                              </>
                            ) : (
                              <>
                                <span className="text-sm text-primary font-bold">
                                  {formatCurrency(service.unit_price_ht)}
                                </span>
                                <span className="text-xs text-muted-foreground">
                                  / {getUnitLabel(service.unit)} • TVA {service.tva_rate}%
                                </span>
                              </>
                            )}
                          </div>
                          {service.notes && (
                            <p className="text-xs text-muted-foreground mt-1 italic">{service.notes}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEdit(service)}
                            className="h-10 w-10 hover:bg-muted"
                            data-testid={`service-edit-btn-${service.id}`}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-10 w-10 text-destructive hover:bg-destructive/20"
                                data-testid={`service-delete-btn-${service.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent className="bg-card border-white/10">
                              <AlertDialogHeader>
                                <AlertDialogTitle>Supprimer ce service ?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Cette action est irréversible.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel className="border-white/10">Annuler</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => handleDelete(service.id)}
                                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                >
                                  Supprimer
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <Card className="bg-card border-white/10">
            <CardContent className="empty-state">
              <Wrench className="w-12 h-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">
                {searchTerm || filterDomain !== 'all' ? 'Aucun résultat' : 'Aucun service'}
              </h3>
              <p className="text-muted-foreground mb-4">
                {searchTerm || filterDomain !== 'all' ? 'Essayez avec d\'autres critères' : 'Créez votre catalogue de prestations'}
              </p>
              {!searchTerm && filterDomain === 'all' && (
                <Button
                  onClick={handleOpenDialog}
                  className="bg-primary text-primary-foreground hover:bg-accent rounded-full"
                  data-testid="add-first-service-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Ajouter un service
                </Button>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default ServicesPage;
